import { type TransferListItem } from '@utazzo/shared';
import { motion } from 'framer-motion';
import { ArrowRight, Bus, Car, Image as ImageIcon, MapPin, Ship, TrainFront } from 'lucide-react';
import React from 'react';

import { LegacyBadge } from '@/components/legacy-badge';
import { fadeUp } from '@/lib/motion';

/**
 * Transfer card - mirrors HotelCard's structural rhythm: hero, title, route
 * strip (from -> to), category / vehicle metadata, and a status + INR price
 * footer with per-person / per-vehicle qualifier.
 */

interface TransferCardProps {
  transfer: TransferListItem;
  onClick?: (transfer: TransferListItem) => void;
}

const STATUS_TONE: Record<TransferListItem['status'], { dot: string; label: string }> = {
  ACTIVE: { dot: 'bg-emerald-500', label: 'Active' },
  DRAFT: { dot: 'bg-amber-500', label: 'Draft' },
  PENDING_APPROVAL: { dot: 'bg-amber-500', label: 'Pending' },
  INACTIVE: { dot: 'bg-zinc-400', label: 'Inactive' },
  ARCHIVED: { dot: 'bg-zinc-400', label: 'Archived' },
};

const TRANSFER_TYPE_LABEL: Record<TransferListItem['transferType'], string> = {
  ONE_WAY: 'One-way',
  ROUND_TRIP: 'Round trip',
  HOURLY: 'Hourly',
  DAY_HIRE: 'Day hire',
  MULTI_STOP: 'Multi-stop',
};

const CATEGORY_LABEL: Record<string, string> = {
  AIRPORT: 'Airport',
  HOTEL: 'Hotel',
  INTERCITY: 'Intercity',
  OTHER: 'Other',
};

const MODE_ICON: Record<TransferListItem['transferMode'], typeof Car> = {
  CAR: Car,
  BUS: Bus,
  TRAIN: TrainFront,
  FERRY: Ship,
};

const fmtInr = (amount: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

export const TransferCard = ({ transfer, onClick }: TransferCardProps): React.JSX.Element => {
  const status = STATUS_TONE[transfer.status];
  const category = transfer.transferCategory
    ? (CATEGORY_LABEL[transfer.transferCategory] ?? transfer.transferCategory)
    : null;
  const serviceLabel =
    transfer.baseRateServiceType === 'SIC'
      ? 'per person'
      : transfer.baseRateServiceType === 'PRIVATE'
        ? 'per vehicle'
        : null;
  const ModeIcon = MODE_ICON[transfer.transferMode];

  return (
    <motion.button
      type="button"
      onClick={() => onClick?.(transfer)}
      variants={fadeUp}
      className={
        'group relative flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white text-left ' +
        'transition-[border-color,background-color] duration-150 ' +
        'hover:border-zinc-300 hover:bg-zinc-50/30 ' +
        'focus-visible:border-zinc-400 focus-visible:ring-2 focus-visible:ring-zinc-300 focus-visible:ring-offset-2 focus-visible:outline-none'
      }
    >
      <div className="relative flex h-28 items-center justify-center overflow-hidden border-b border-zinc-100 bg-zinc-50">
        {transfer.imageUrl ? (
          <img
            src={transfer.imageUrl}
            alt={transfer.name}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <ImageIcon className="h-6 w-6 text-zinc-300" strokeWidth={1.5} aria-hidden />
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 shadow-sm ring-1 ring-zinc-200/80">
          <ModeIcon className="h-3 w-3 text-zinc-500" strokeWidth={2} aria-hidden />
          <span className="text-[10px] font-medium tracking-wide text-zinc-600">
            {TRANSFER_TYPE_LABEL[transfer.transferType]}
          </span>
        </span>
        {category && (
          <span className="absolute top-2 right-2 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-zinc-600 ring-1 ring-zinc-200/80">
            {category}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start gap-2">
          <h3 className="line-clamp-1 flex-1 text-sm leading-snug font-semibold tracking-tight text-zinc-900">
            {transfer.name}
          </h3>
          <LegacyBadge legacyId={transfer.legacyId} />
        </div>

        <div className="flex flex-col gap-0.5">
          {(transfer.fromLocationName || transfer.toLocationName) && (
            <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
              <span className="truncate">{transfer.fromLocationName ?? 'unset'}</span>
              <ArrowRight className="h-3 w-3 shrink-0 text-zinc-400" strokeWidth={2} aria-hidden />
              <span className="truncate">{transfer.toLocationName ?? 'unset'}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
            <MapPin className="h-3 w-3 shrink-0 text-zinc-400" strokeWidth={2} aria-hidden />
            <span className="truncate">
              {transfer.cityName}
              {transfer.countryName ? `, ${transfer.countryName}` : ''}
            </span>
          </span>
        </div>

        <div className="mt-auto flex items-center gap-3 border-t border-zinc-100 pt-3 text-xs">
          <span className="inline-flex items-center gap-1.5 text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + status.dot} aria-hidden />
            {status.label}
          </span>
          {transfer.vendorSupplierName && (
            <>
              <span className="text-zinc-300" aria-hidden>
                ·
              </span>
              <span
                className="max-w-[110px] truncate text-zinc-500"
                title={transfer.vendorSupplierName}
              >
                {transfer.vendorSupplierName}
              </span>
            </>
          )}
          <span className="flex-1" />
          {transfer.baseRatePriceInr > 0 ? (
            <span className="text-right">
              <span className="block text-[9px] font-medium tracking-wider text-zinc-400 uppercase">
                From
              </span>
              <span className="block text-sm font-semibold text-zinc-900 tabular-nums">
                {fmtInr(transfer.baseRatePriceInr)}
              </span>
              {serviceLabel && (
                <span className="block text-[9px] text-zinc-400">{serviceLabel}</span>
              )}
            </span>
          ) : (
            <span className="text-[10px] text-zinc-400">No rate</span>
          )}
        </div>
      </div>
    </motion.button>
  );
};
